import { Bot, KeyRound, Plug } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiKeys as keysApi, developers as devApi } from '../api/resources.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs.js';
import { Card, CopyButton, Empty, ErrorState, Loading } from '../components/ui.js';
import { useAsync } from '../lib/hooks.js';

/**
 * Página de conexão do MCP (docs/mega-panel.md, D4): o que é, como conectar cada
 * cliente e quais ferramentas existem. A chave nunca aparece aqui — o snippet leva um
 * marcador, e quem cola troca pela chave que guardou quando a criou.
 */

const CLIENTS = ['claude', 'cursor', 'json'] as const;
type Client = (typeof CLIENTS)[number];
const CLIENT_LABEL: Record<Client, string> = {
  claude: 'Claude Code',
  cursor: 'Cursor',
  json: 'JSON genérico',
};

export function McpPage() {
  const tools = useAsync(() => devApi.mcpTools(), []);
  const keys = useAsync(() => keysApi.list(), []);
  const [client, setClient] = useState<Client>('claude');

  const endpoint = `${window.location.origin}/v1/mcp`;
  const placeholder = 'tk_live_SUA_CHAVE_AQUI';
  const snippet = snippetFor(client, endpoint, placeholder);
  const activeKeys = (keys.data?.data ?? []).filter((key) => !key.revokedAt);

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="m-0 text-xl font-semibold">Servidor MCP</h2>
          <p className="m-0 text-sm text-muted-foreground">
            Conecte o Claude Code, o Cursor ou qualquer agente ao WhatsApp da sua organização.
          </p>
        </div>
        <Badge tone="brand">Model Context Protocol</Badge>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card
          title="Conectar"
          description="O servidor fala JSON-RPC sobre HTTP e autentica com uma API key. Cada ferramenta respeita as permissões da chave."
          actions={
            <Tabs value={client} onValueChange={(value) => setClient(value as Client)}>
              <TabsList>
                {CLIENTS.map((key) => (
                  <TabsTrigger key={key} value={key}>
                    {CLIENT_LABEL[key]}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          }
        >
          <div className="relative">
            <div className="absolute top-2 right-2">
              <CopyButton value={snippet} label="Copiar configuração" />
            </div>
            <pre className="code text-[12px]" data-testid="mcp-snippet">
              {snippet}
            </pre>
          </div>
          <ol className="mt-4 mb-0 flex flex-col gap-1.5 pl-5 text-sm text-muted-foreground">
            <li>
              Crie uma API key em <Link to="/developers/keys">API Keys</Link>
              {activeKeys.length > 0 && (
                <>
                  {' '}
                  — você já tem {activeKeys.length} ativa{activeKeys.length === 1 ? '' : 's'} (
                  {activeKeys.map((key) => key.name).join(', ')})
                </>
              )}
              . Uma chave só de leitura limita o agente a consultar.
            </li>
            <li>
              Troque <code>{placeholder}</code> pela chave. Ela é mostrada uma única vez, na
              criação.
            </li>
            <li>Peça ao agente: “liste minhas instâncias” — e depois “envie um oi para 5511…”.</li>
          </ol>
          <div className="mt-4 flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            <KeyRound className="size-4 shrink-0" aria-hidden="true" />
            Endpoint: <code className="font-mono">{endpoint}</code>
            <CopyButton value={endpoint} label="Copiar endpoint" variant="ghost" size="icon-xs" />
          </div>
        </Card>

        <Card title="Ferramentas" description="O que o agente consegue fazer." bodyless>
          {tools.loading ? (
            <Loading />
          ) : tools.error ? (
            <ErrorState error={tools.error} onRetry={tools.reload} />
          ) : (tools.data?.data.length ?? 0) === 0 ? (
            <Empty title="Nenhuma ferramenta" icon={<Bot />} />
          ) : (
            <ul className="m-0 list-none divide-y divide-border p-0">
              {(tools.data?.data ?? []).map((tool) => (
                <li key={tool.name} className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <Plug className="size-3.5 text-primary" aria-hidden="true" />
                    <code className="font-mono text-[13px] font-medium">{tool.name}</code>
                  </div>
                  <p className="mt-0.5 mb-0 text-xs text-muted-foreground">{tool.description}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card title="Testar sem agente">
        <p className="m-0 mb-2 text-sm text-muted-foreground">
          Qualquer cliente HTTP serve. Este comando lista as ferramentas com a sua chave:
        </p>
        <div className="relative">
          <div className="absolute top-2 right-2">
            <CopyButton value={curlFor(endpoint, placeholder)} label="Copiar comando" />
          </div>
          <pre className="code text-[12px]">{curlFor(endpoint, placeholder)}</pre>
        </div>
        <div className="mt-3">
          <Button variant="outline" size="sm" asChild>
            <Link to="/developers/explorer" className="no-underline">
              Prefere REST? Abra o API Explorer
            </Link>
          </Button>
        </div>
      </Card>
    </>
  );
}

function snippetFor(client: Client, endpoint: string, key: string): string {
  switch (client) {
    case 'claude':
      return `claude mcp add --transport http talkium ${endpoint} \\\n  --header "Authorization: Bearer ${key}"`;
    case 'cursor':
      return JSON.stringify(
        { mcpServers: { talkium: { url: endpoint, headers: { Authorization: `Bearer ${key}` } } } },
        null,
        2,
      );
    default:
      return JSON.stringify(
        {
          talkium: {
            transport: 'http',
            url: endpoint,
            headers: { Authorization: `Bearer ${key}` },
          },
        },
        null,
        2,
      );
  }
}

function curlFor(endpoint: string, key: string): string {
  return [
    `curl -X POST '${endpoint}' \\`,
    `  -H 'Authorization: Bearer ${key}' \\`,
    "  -H 'Content-Type: application/json' \\",
    `  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}'`,
  ].join('\n');
}
